import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useProfile } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import {
  cardBalanceView,
  fetchCcDebtByAccountScoped,
  fetchCcUnappliedByCard,
  onAccountCreditApplies,
} from "@/lib/cc";
import { useIsSolo } from "@/hooks/useIsSolo";
import { CcPayDialog } from "@/components/CcPayDialog";
import { CcInterestFreeNotice } from "@/components/CcInterestFreeNotice";
import { ConciliarResumenDialog } from "@/components/ConciliarResumenDialog";
import { cardsQueryKey, ccDebtByAccountQueryKey } from "@/lib/authScopedQueryKeys";
import { getFinancialInstitution } from "@/lib/financialInstitutions";

export const Route = createFileRoute("/_app/cards")({ component: CardsPage });

function CardsPage() {
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const profileId = profile?.id;
  const { viewMode } = useAppStore();
  const { isSolo } = useIsSolo();
  const scope: "household" | "personal" =
    isSolo ? "household" : (viewMode === "personal" ? "personal" : "household");

  const dataQ = useQuery({
    queryKey: cardsQueryKey(hh ?? "", profileId ?? ""),
    queryFn: async () => {
      const { data: accounts, error: accountsError } = await supabase.from("accounts").select("*").eq("household_id", hh!).eq("type", "credit_card").eq("is_active", true);
      if (accountsError) throw accountsError;
      const ids = (accounts ?? []).map((a) => a.id);
      const { data: details, error: detailsError } = ids.length ? await supabase.from("credit_card_details").select("*").in("account_id", ids) : { data: [], error: null };
      if (detailsError) throw detailsError;
      const { data: bankAccounts, error: bankAccountsError } = await supabase.from("accounts").select("*").eq("household_id", hh!).neq("type", "credit_card").eq("is_active", true);
      if (bankAccountsError) throw bankAccountsError;
      return { cards: accounts ?? [], details: details ?? [], banks: bankAccounts ?? [] };
    },
    enabled: !!hh && !!profileId,
  });
  const debtQ = useQuery({
    queryKey: ccDebtByAccountQueryKey(hh ?? "", profileId ?? "", scope),
    queryFn: () => fetchCcDebtByAccountScoped(scope),
    enabled: !!hh && !!profileId,
  });

  // P1: cuánto de la deuda de cada tarjeta son pagos a cuenta sin aplicar. Sólo informa: la deuda ya viene neta.
  const unappliedQ = useQuery({
    queryKey: ["cc-unapplied-by-card", hh, profileId],
    queryFn: fetchCcUnappliedByCard,
    enabled: !!hh && !!profileId,
  });

  const [payOpen, setPayOpen] = useState(false);
  const [payCardId, setPayCardId] = useState<string | null>(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileCard, setReconcileCard] = useState<{
    id: string;
    name: string;
    currency: string;
    institution_code: string | null;
    is_household_account: boolean;
    /** Día de corte (credit_card_details). null = no se pudo leer / sin configurar. */
    closing_day: number | null;
    /** C2 Decisión 25: para saber si es una tarjeta PERSONAL propia (espejo de P3/Decisión 18). */
    owner_id: string | null;
    is_shared: boolean;
  } | null>(null);

  const detailMap = new Map((dataQ.data?.details ?? []).map((d) => [d.account_id, d]));
  const visibleCards = (dataQ.data?.cards ?? []).filter((c) => viewMode === "household" ? true : (!c.owner_id || c.owner_id === profile?.id));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Tarjetas de Crédito</h1>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {visibleCards.map((c) => {
          const d = detailMap.get(c.id);
          // Fuente de verdad: cuotas pendientes en cc_charges (vista cc_account_debt).
          // accounts.balance quedó obsoleto para TC tras el backfill.
          const debt = Number(debtQ.data?.get(c.id)?.debt ?? 0);
          // Un pago a cuenta cuenta en el alcance de la tarjeta (hogar → Hogar; personal → Personal del dueño):
          // se muestra sólo donde la deuda de arriba lo restó de verdad.
          const unappliedRaw = onAccountCreditApplies(c, scope, profileId)
            ? (unappliedQ.data?.get(c.id) ?? 0)
            : 0;
          const view = cardBalanceView(debt, unappliedRaw);
          const limit = Number(d?.credit_limit ?? 0);
          const used = limit > 0 ? (Math.max(debt, 0) / limit) * 100 : 0;
          const usedDisplay = limit > 0 ? `${Math.min(used, 999).toFixed(0)}%` : "—";
          return (
            <Card key={c.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {getFinancialInstitution(c.institution_code)?.label ?? (
                        <span className="text-amber-600 dark:text-amber-400">
                          Institución sin configurar
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setReconcileCard({
                          id: c.id,
                          name: c.name,
                          currency: c.currency,
                          institution_code: c.institution_code,
                          is_household_account: c.is_household_account === true,
                          closing_day: d?.closing_day ?? null,
                          owner_id: c.owner_id ?? null,
                          is_shared: c.is_shared === true,
                        });
                        setReconcileOpen(true);
                      }}
                    >
                      Conciliar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPayCardId(c.id);
                        setPayOpen(true);
                      }}
                    >
                      Pagar
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => navigate({ to: "/accounts", search: { editAccountId: c.id } })}
                      title="Editar tarjeta"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {view.credit > 0 ? (
                  <>
                    <div className="mt-3 text-2xl font-bold text-income">
                      {formatCurrency(view.credit, c.currency)}
                    </div>
                    <div className="text-xs text-muted-foreground">Saldo a favor</div>
                  </>
                ) : (
                  <>
                    <div className="mt-3 text-2xl font-bold text-expense">
                      {formatCurrency(view.owed, c.currency)}
                    </div>
                    <div className="text-xs text-muted-foreground">Deuda actual</div>
                  </>
                )}
                {view.unapplied > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Incluye {formatCurrency(view.unapplied, c.currency)} de pagos a cuenta sin
                    aplicar
                  </div>
                )}
                <CcInterestFreeNotice cardId={c.id} currency={c.currency} />
                {d && (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground">Cierre</div>
                      <div className="font-medium">Día {d.closing_day}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Vence</div>
                      <div className="font-medium">Día {d.due_day}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Uso</div>
                      <div className="font-medium">{usedDisplay}</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {visibleCards.length === 0 && <div className="text-sm text-muted-foreground">No tienes tarjetas registradas. Agrégalas desde Cuentas.</div>}
      </div>

      <CcPayDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        cardAccountId={payCardId}
        cards={(dataQ.data?.cards ?? []) as any}
        banks={(dataQ.data?.banks ?? []) as any}
      />

      <ConciliarResumenDialog
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        card={reconcileCard}
      />
    </div>
  );
}
